import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase';
import './Navbar.css';

const Navbar = ({ onNavigate, onNavigateToService }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null); // ana başlık (category id)
  const [hoveredSub, setHoveredSub] = useState(null); // { categoryId, subCategoryId } → sağda hizmetler
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const categoriesRef = ref(database, 'productCategories');
    const unsub = onValue(categoriesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data)
          .map(key => {
            const cat = data[key];
            let subCategories = [];
            if (cat.subCategories && Object.keys(cat.subCategories).length > 0) {
              subCategories = Object.keys(cat.subCategories).map(sid => ({
                id: sid,
                title: cat.subCategories[sid].title || '',
                order: cat.subCategories[sid].order ?? 999,
                items: cat.subCategories[sid].items ? Object.keys(cat.subCategories[sid].items).map(iid => ({
                  id: iid,
                  title: cat.subCategories[sid].items[iid].title || ''
                })) : []
              })).sort((a, b) => a.order - b.order);
            } else if (cat.items && Object.keys(cat.items).length > 0) {
              subCategories = [{
                id: 'genel',
                title: 'Genel',
                items: Object.keys(cat.items).map(iid => ({ id: iid, title: cat.items[iid].title || '' }))
              }];
            }
            return {
              id: key,
              title: cat.title || '',
              order: cat.order ?? 999,
              subCategories
            };
          })
          .sort((a, b) => a.order - b.order);
        setCategories(list);
      } else {
        setCategories([]);
      }
    });
    return () => unsub();
  }, []);

  const [expandedSubMobile, setExpandedSubMobile] = useState(null);

  const isMobileMenu = mobileMenuOpen;
  const handleServicesLinkClick = (e) => {
    e.preventDefault();
    handleNav('products', 'products');
    setServicesOpen(false);
    setExpandedSubMobile(null);
  };

  const handleServicesMouseEnter = () => {
    if (!isMobileMenu) setServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    if (!isMobileMenu) {
      setServicesOpen(false);
      setHoveredCategory(null);
      setHoveredSub(null);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleNav = (page, sectionId) => {
    if (onNavigate) {
      onNavigate(page);
      setMobileMenuOpen(false);
      setServicesOpen(false);
    } else if (sectionId) {
      scrollToSection(sectionId);
    }
  };

  const hoveredCat = hoveredCategory ? categories.find(c => c.id === hoveredCategory) : null;
  const middleItems = hoveredCat ? (hoveredCat.subCategories || []) : [];
  const rightItems = (() => {
    if (!hoveredSub) return [];
    const cat = categories.find(c => c.id === hoveredSub.categoryId);
    if (!cat || !cat.subCategories) return [];
    const sub = cat.subCategories.find(s => s.id === hoveredSub.subCategoryId);
    if (!sub || !sub.items) return [];
    return sub.items.map(item => ({
      ...item,
      categoryId: cat.id,
      subCategoryId: sub.id
    }));
  })();

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <div className="navbar-logo" onClick={() => handleNav('home', 'hero')}>
          <img
            src="/logoiso.png"
            alt="İso Belgesi"
            className="navbar-logo-image"
          />
        </div>

        <div className={`navbar-menu ${mobileMenuOpen ? 'active' : ''}`}>
          <a href="#hero" onClick={(e) => { e.preventDefault(); handleNav('home', 'hero'); }}>Ana Sayfa</a>
          <a href="#about" onClick={(e) => { e.preventDefault(); handleNav('about'); }}>Hakkımızda</a>
          <div
            className="navbar-dropdown-wrap"
            onMouseEnter={handleServicesMouseEnter}
            onMouseLeave={handleServicesMouseLeave}
          >
            <a
              href="#services"
              onClick={handleServicesLinkClick}
              className={servicesOpen ? 'active' : ''}
              aria-expanded={isMobileMenu ? servicesOpen : undefined}
            >
              İSO Belgeleri {isMobileMenu ? (servicesOpen ? '−' : '+') : '+'}
            </a>
            {servicesOpen && categories.length > 0 && !isMobileMenu && (
              <div className="navbar-mega">
                <div className={`navbar-mega-inner navbar-mega-inner--cols-${hoveredSub ? 3 : hoveredCategory ? 2 : 1}`}>
                  {/* 1. Adım: Sadece ana başlıklar */}
                  <div className="navbar-mega-left">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        className={`navbar-mega-ana ${hoveredCategory === cat.id ? 'active' : ''}`}
                        onMouseEnter={() => {
                          setHoveredCategory(cat.id);
                          setHoveredSub(null);
                        }}
                        onClick={() => handleNav('products', 'products')}
                      >
                        {cat.title}
                      </button>
                    ))}
                  </div>
                  {/* 2. Adım: Ana başlığa gelince orta sütun açılır */}
                  {hoveredCategory && (
                    <div className="navbar-mega-middle">
                      {middleItems.length === 0 && hoveredCat ? (
                        <p className="navbar-mega-empty">Bu kategoride alt başlık yok.</p>
                      ) : (
                        middleItems.map((sub) => (
                          <button
                            key={sub.id}
                            type="button"
                            className={`navbar-mega-sub ${hoveredSub && hoveredSub.categoryId === hoveredCategory && hoveredSub.subCategoryId === sub.id ? 'active' : ''}`}
                            onMouseEnter={() => setHoveredSub({ categoryId: hoveredCategory, subCategoryId: sub.id })}
                            onClick={() => handleNav('products', 'products')}
                          >
                            {sub.title}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  {/* 3. Adım: Alt kategoriye gelince sağ sütun açılır */}
                  {hoveredSub && (
                    <div className="navbar-mega-right">
                      {rightItems.length === 0 ? (
                        <p className="navbar-mega-empty">Bu kategoride henüz hizmet yok.</p>
                      ) : (
                        rightItems.map((item) => (
                          <a
                            key={item.id}
                            href="#services"
                            className="navbar-mega-item"
                            onClick={(e) => {
                              e.preventDefault();
                              if (onNavigateToService) {
                                onNavigateToService(item.categoryId, item.subCategoryId, item.id);
                                setServicesOpen(false);
                              } else {
                                handleNav('products', 'products');
                              }
                            }}
                          >
                            {item.title}
                          </a>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {categories.length > 0 && isMobileMenu && (
              <div className={`navbar-mobile-services ${servicesOpen ? 'navbar-mobile-services--open' : ''}`}>
                {categories.map((cat) => (
                  <div key={cat.id} className="navbar-mobile-cat">
                    <span className="navbar-mobile-cat-title">{cat.title}</span>
                    {(cat.subCategories || []).map((sub) => {
                      const isExpanded = expandedSubMobile && expandedSubMobile.categoryId === cat.id && expandedSubMobile.subCategoryId === sub.id;
                      const items = (sub.items || []).map(item => ({
                        ...item,
                        categoryId: cat.id,
                        subCategoryId: sub.id
                      }));
                      return (
                        <div key={sub.id} className="navbar-mobile-sub-wrap">
                          <button
                            type="button"
                            className="navbar-mobile-sub-btn"
                            onClick={() => setExpandedSubMobile(isExpanded ? null : { categoryId: cat.id, subCategoryId: sub.id })}
                          >
                            {sub.title} {items.length ? `(${items.length})` : ''}
                          </button>
                          {isExpanded && items.length > 0 && (
                            <div className="navbar-mobile-items">
                              {items.map((item) => (
                                <a
                                  key={item.id}
                                  href="#services"
                                  className="navbar-mobile-item"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (onNavigateToService) {
                                      onNavigateToService(item.categoryId, item.subCategoryId, item.id);
                                      setServicesOpen(false);
                                      setMobileMenuOpen(false);
                                    } else {
                                      handleNav('products', 'products');
                                    }
                                  }}
                                >
                                  {item.title}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
          <a href="#sss" onClick={(e) => { e.preventDefault(); handleNav('faq', 'sss'); }}>S.S.S.</a>
          <a href="#blog" onClick={(e) => { e.preventDefault(); handleNav('blog', 'blog'); }}>Blog</a>
          <a href="#haber" onClick={(e) => { e.preventDefault(); handleNav('news', 'haber'); }}>Haberler</a>
          <a href="#contact" onClick={(e) => { e.preventDefault(); handleNav('contact', 'contact'); }}>İletişim</a>
        </div>

        <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
